import { useCallback, useContext, useEffect, useState } from "react";
import { NavContext } from "../contexts/NavContextProvider";
import { updateDocumentTitle } from "..";
import SubPageTitle from "../SubPageTitle";
import { Button, InfoLabel, makeStyles, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Toast, ToastBody, Toaster, ToastTitle, tokens, Toolbar, useId, useToastController } from "@fluentui/react-components";
import LinkWithIcon from "../LinkWithIcon";
import { AddFilled, AddRegular, AppsFilled, bundleIcon } from "@fluentui/react-icons";
import NewProductDrawer, { EnabledProductFeatureFlags } from "../drawers/NewProductDrawer";

const useStyles = makeStyles({
    toolbar: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        padding: "0.25rem"
    }
});

const AddIcon = bundleIcon( AddFilled, AddRegular );

export default function ProductsPage() {
    const styles = useStyles();
    const { navData, setNavData } = useContext(NavContext);
    const [ newProductOpen, setNewProductOpen ] = useState(false);
    const toasterId = useId( "toaster" );
    const { dispatchToast, updateToast } = useToastController( toasterId );
    const createProductToastId = useId( "createProductToast" );
    const [ products, setProducts ] = useState( [] );

    useEffect(
        () => {
            updateDocumentTitle( "Products | All Products" );
            navData.sidebarSelectedNavValue = "products";
            navData.subPageNavValue = null;
            setNavData({...navData});

            fetch(
                "https://api.violetech.co.uk/v1/flighting/products",
                {
                    headers: {
                        "Authorization": `Bearer ${localStorage.getItem( "authToken" )}`
                    },
                    method: "GET"
                }
            ).then(
                ( data ) => {
                    if( data.status != 200 ) {
                        console.error( "Failed to fetch products", data );
                        return;
                    }

                    data.json().then(
                        ( json ) => {
                            setProducts( json );
                        }
                    );
                }
            );
        },
        []
    );

    const newProduct = useCallback(
        () => {
            setNewProductOpen( true );
        },
        []
    );

    const newProductCallback = useCallback(
        ( name: string, enabledFeatures: EnabledProductFeatureFlags ) => {
            dispatchToast(
                <Toast>
                    <ToastTitle media={<Spinner size="tiny" />}>Creating Product...</ToastTitle>
                    <ToastBody>
                        We're creating your product.
                    </ToastBody>
                </Toast>,
                {
                    timeout: -1,
                    toastId: createProductToastId
                }
            );

            setTimeout(
                () => {
                    updateToast(
                        {
                            toastId: createProductToastId,
                            content: 
                                <Toast>
                                    <ToastTitle>Product Created</ToastTitle>
                                    <ToastBody>
                                        Your product has been created successfully.
                                    </ToastBody>
                                </Toast>,
                            timeout: 3000,
                            intent: "success"
                        }
                    );
                },
                2000
            );
        },
        []
    );

    return (
        <>
            <SubPageTitle icon={<AppsFilled />} title="Products" subTitle="All Products" />

            <Toolbar style={{ marginTop: "1rem" }} className={styles.toolbar}>
                <Button appearance="subtle" icon={<AddIcon />} onClick={newProduct}>
                    New Product
                </Button>
            </Toolbar>

            <Table style={{ marginTop: "1rem" }}>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>
                            <InfoLabel label="Active Clients" info={"Client machines that have used this product within the last 30 days. Blocked clients are not included."} />
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {
                        products.map(
                            ( product: any ) => (
                                <TableRow key={product.id}>
                                    <TableCell><LinkWithIcon href={`/products/${product.id}`} text={product.name} /></TableCell>
                                    <TableCell><LinkWithIcon href={`/products/${product.id}#clients`} text={"0"} /></TableCell>
                                </TableRow>
                            )
                        )
                    }
                </TableBody>
            </Table>

            <Toaster toasterId={toasterId} position="top-end" />
            <NewProductDrawer open={newProductOpen} setOpen={setNewProductOpen} createProductCallback={newProductCallback} />
        </>
    );
}